// src/lib/auth.ts
/**
 * Единая точка «кто я?».
 * Источники:
 * 1) Telegram.WebApp.initData (основной путь)
 * 2) ?tgWebAppData=... (иногда Telegram кладёт initData в query)
 * 3) ?tg_id=... (dev-фоллбэк)
 * 4) VITE_TG_DEBUG_ID (локальная отладка в обычном браузере)
 *
 * Также экспортируем fetchWithInit, который добавляет:
 *  - X-Telegram-Init-Data: <initData>  (если есть)
 *  - иначе подставляет ?tg_id=... в URL (если знаем tg_id)
 */

type TwaUser = {
  id: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  language_code?: string;
  is_premium?: boolean;
  allows_write_to_pm?: boolean;
  photo_url?: string;
};

type InitUnsafe = {
  user?: TwaUser;
  query_id?: string;
  auth_date?: string | number;
  hash?: string;
  start_param?: string;
};

const TWA = (globalThis as any)?.Telegram?.WebApp;

/** Пытаемся достать initData из URL (?tgWebAppData=...) — иногда Телега кладёт его туда */
function readInitDataFromUrl(): string | undefined {
  try {
    const u = new URL(globalThis.location.href);
    const alt = u.searchParams.get("tgWebAppData");
    if (alt && alt.trim().length > 0) {
      return alt;
    }
  } catch {}
  return undefined;
}

// --- dynamic getters for initData and tg_id ---
function readTwa() {
  try { return (globalThis as any)?.Telegram?.WebApp || null; } catch { return null; }
}

/** Вернуть сырой initData (если есть) — каждый раз читаем из TWA или URL */
export function getInitDataRaw(): string | undefined {
  const twa = readTwa();
  const fromTwa: string | undefined = (twa && twa.initData) || undefined;
  if (fromTwa && fromTwa.trim().length > 0) return fromTwa;
  const fromUrl = readInitDataFromUrl();
  return fromUrl && fromUrl.trim().length > 0 ? fromUrl : undefined;
}

/** Вернуть initDataUnsafe.user.id, если доступен прямо сейчас */
function getTwaUserId(): number | undefined {
  try {
    const twa = readTwa();
    const u = (twa && (twa as any).initDataUnsafe && (twa as any).initDataUnsafe.user) || null;
    const id = u?.id;
    return typeof id === 'number' && isFinite(id) ? id : undefined;
  } catch { return undefined; }
}

/** tg_id: сначала из TWA, затем из query (?tg_id=…), затем из .env (dev) */
export function getCurrentTgId(): string | undefined {
  // 1) from TWA
  const fromTwa = getTwaUserId();
  if (fromTwa) return String(fromTwa);
  // 2) from query
  try {
    const url = new URL(globalThis.location.href);
    const q = url.searchParams.get('tg_id');
    if (q && q.trim()) return q;
  } catch {}
  // 3) from env (dev only)
  const env = (import.meta as any)?.env?.VITE_TG_DEBUG_ID;
  if (env && String(env).trim() !== '') return String(env);
  return undefined;
}

/** Признак наличия initData (динамически) */
export function hasInitData(): boolean {
  const raw = getInitDataRaw();
  return Boolean(raw && raw.length > 0);
}

/**
 * Обёртка fetch: добавляет X-Telegram-Init-Data если есть initData,
 * иначе аккуратно прописывает ?tg_id=... в URL (сохраняя остальные query).
 */
export async function fetchWithInit(
  input: string | URL | Request,
  init?: RequestInit
): Promise<Response> {
  let reqUrl =
    typeof input === "string"
      ? input
      : input instanceof URL
      ? input.toString()
      : (input as Request).url;

  const headers = new Headers(init?.headers || {});
  const initHeader = getInitDataRaw();
  const dynTgId = getCurrentTgId();

  if (initHeader) {
    headers.set("X-Telegram-Init-Data", initHeader);
  } else if (dynTgId) {
    // добавим/заменим tg_id в query
    const u = new URL(reqUrl, globalThis.location.origin);
    u.searchParams.set("tg_id", String(dynTgId));
    reqUrl = u.toString();
  }

  return fetch(reqUrl, { ...init, headers });
}

/** Быстрая проверка роли на клиенте */
export function isStaff(role?: string | null): boolean {
  const r = String(role || "").toLowerCase();
  return r === "door" || r === "staff" || r === "admin" || r === "superadmin" || r === "owner";
}